import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useBusinessContext } from './BusinessContext';
import axios from 'axios';

const PermissionContext = createContext({});

export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within PermissionContextProvider');
  }
  return context;
};

export const PermissionContextProvider = ({ children }) => {
  const { user } = useAuth();
  const { currentBusinessId } = useBusinessContext();
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch permissions for viewers
  useEffect(() => {
    const fetchPermissions = async () => {
      if (!user || user.role !== 'viewer') {
        // Non-viewers have all permissions
        setPermissions(null);
        return;
      }

      // Don't fetch if business context isn't set yet - wait for BusinessContext to set it
      if (!currentBusinessId) {
        return;
      }

      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/viewer/my-permissions`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'X-Business-Id': currentBusinessId.toString()
            }
          }
        );

        if (response.data.success) {
          setPermissions(response.data.permissions);
        } else {
          console.error('[PermissionContext] Failed to fetch viewer permissions');
          setPermissions({});
        }
      } catch (error) {
        console.error('[PermissionContext] Error fetching viewer permissions', error);
        setPermissions({});
      } finally {
        setLoading(false);
      }
    };

    fetchPermissions();
  }, [user, currentBusinessId]);

  // Check if user has a specific permission
  const hasPermission = (permissionKey) => {
    // Non-viewers always have permission
    if (!user || user.role !== 'viewer') {
      return true;
    }

    // If permissions not loaded yet, deny
    if (!permissions) {
      return false;
    }

    return permissions[permissionKey] === true;
  };

  // Check multiple permissions (OR logic)
  const hasAnyPermission = (...permissionKeys) => {
    return permissionKeys.some(key => hasPermission(key));
  };

  // Check multiple permissions (AND logic)
  const hasAllPermissions = (...permissionKeys) => {
    return permissionKeys.every(key => hasPermission(key));
  };

  const value = {
    permissions,
    loading,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isViewer: user?.role === 'viewer'
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};
